(ns frontend.view.components.power-search
  (:require
   [clojure.string :as string]
   [common.entity :as entity]
   [common.intelligence.common :as ic]
   [common.localization :as loc]
   [common.schema.entity-type-names :as entity-type-names]
   [common.schema.entity-types :as entity-types]
   [common.util :as cu :refer [flip]]
   [frontend.db :as app-db]
   [frontend.entity-utils :as eu]
   [frontend.routing :as routing]
   [frontend.state.current-session :as session]
   [frontend.utils :refer [stop-propagation-event!]]
   [frontend.utils :as fu]
   [goog.events :as goog-events]
   [reagent.core :as r]
   [reagent.ratom :as ratom]))

(defonce state (r/cursor app-db/app-state [:power-search]))

(def searchable-entity-types
  [;; simple global permissions
   :entity.type/person
   :entity.type/company
   :entity.type/fof-asset
   :entity.type/investor
   :entity.type/other-asset
   :entity.type/real-estate

   ;; team specific access
   :entity.type/fund
   :entity.type/fund-commitment

   ;; pipeline specific permissions
   :entity.type/oppo
   :entity.type/fundraising-opportunity
   :entity.type/sales-opportunity
   :entity.type/exit-opportunity

;; module based access
   :entity.type/holding
   :entity.type/kyc
   :entity.type/sale-agreement])

(defn start-search!
  []
  (swap! state assoc :search-open? true)
  (swap! app-db/app-state assoc :searching true))

(defn end-search!
  [timeout]
  (when (:search-open? @state)
    (js/setTimeout
     (fn [_]
       (swap! state dissoc
              :search
              :results
              :blur-emulator
              :timeout
              :search-open?
              :expanded-kw)
       (js/setTimeout #(swap! app-db/app-state dissoc :searching) 10))
     timeout))
  nil)

(defn set-search-input!
  [search-str]
  (swap! state assoc :search (string/lower-case search-str)))

(defn entity-recent-view
  [entity]
  [:li 
   {:class "p-2 flex items-base gap-3 hover:bg-primary-light rounded cursor-pointer group"
    :on-click (fn []
                (routing/open-entity (:db/id entity)))}
   [:div
    [:i
     {:class-name (fu/cs "fal"
                         (get-in entity-types/entities [(:entity/type entity) :entity/icon]))}]]
   [:div
    {:class "flex flex-col gap-1"}
    [:div
     {:class "font-bold group-hover:text-base-light-text-accent"}
     (entity/resolve-entity-name entity session/get-entity session/reverse-lookup)]
    [:div
     {:class "m-0 text-input-light-text-paler text-sm"}
     ;;TODO cleanup. doesn't necessarily have anything to do with contacts, and
     ;;     not really a view if cannot be used with vector notation [eu/entity-contact-view ...]
     (eu/entity-contact-view entity session/get-entity session/reverse-lookup)]]])

(defonce r-recently-created
  (ratom/reaction
   (->> @session/entities-list
        (sort-by :common/created-on)
        (reverse)
        (filter (fn [e]
                  (some #{(:entity/type e)} searchable-entity-types)))
        (take 12)
        doall)))

(defn recently-created-entities
  []
  (let [recent-entities @r-recently-created]
    [:ul
     {:class "flex flex-col gap-2"}
     (doall
      (for [{:keys [db/id] :as entity} recent-entities]
        ^{:key (str "power-search-recently-created-" id)}
        [entity-recent-view entity]))]))

(defonce r-recently-updated
  (ratom/reaction
   (->> @session/entities-list
        (sort-by :common/updated-on)
        (reverse)
        (filter (fn [e]
                  (some #{(:entity/type e)} searchable-entity-types)))
        (take 12)
        doall)))

(defn recently-modified-entities
  []
  (let [recent-entities @r-recently-updated]
    [:ul
     {:class "flex flex-col gap-2"}
     (doall
      (for [{:keys [db/id] :as entity} recent-entities]
        ^{:key (str "power-search-recently-updated-" id)}
        [entity-recent-view entity]))]))

(defn recent-view
  []
  [:div.p-4
   [:div
    {:class-name "grid lg:grid-cols-2 gap-2 pt-3"}
    [:div
     {:class "px-6 flex flex-col gap-3"}
     [:h4
      {:class "font-bold text-2xl"}
      (loc/tr :power-search/dropdown-created-column)]
     [:div 
      [recently-created-entities]]]
    [:div  
     {:class "px-6 flex flex-col gap-3"}
     [:h4
      {:class "font-bold text-2xl"}
      (loc/tr :power-search/dropdown-modified-column)]
     [:div
      [recently-modified-entities]]]
    ;[:div
     ;[:h4.power-search-results__title               ;;TODO must be implemented within 2nd release
     ; (loc/tr :power-search/dropdown-records-column)]
     ;[:div.power-search-results__list] ]
    ]])

(defn search-input
  [on-click search force-open?]
  (let [focus-state (atom {})]
    (fn [on-click search force-open?]
      [:input
       {:class-name  (str "form-control power-search-box" (when force-open? " force-search-visible"))
        :ref         (fn [dom-ref]
                       (when (and dom-ref
                                  (not= dom-ref (:dom-ref @focus-state)))
                         (swap! focus-state assoc :dom-ref dom-ref)
                         (when force-open?
                           (.focus dom-ref))))
        :type        "text"
        :on-click    on-click
        :value       search
        :placeholder (loc/tr :search-default-value)
        :on-focus    (fn []
                       (start-search!))
        :on-change   (fn [e]
                       (set-search-input! (-> e
                                              .-target
                                              .-value)))}])))

(defn info-block-more-chars
  []
  [:div.power-search-results__not-found
   [:img
    {:src        "/img/undraw_File_searching_re_3evy.svg"
     :class-name "img-fluid"}]

   [:span.d-block.pt-4 (loc/tr :power-search/trigger-search)]])

(defn info-block-no-result-found
  []
  [:div.power-search-results__not-found
   [:img
    {:src        "/img/no-data-created.svg"
     :class-name "img-fluid"}]

   [:span.d-block.pt-4 (loc/tr :power-search/no-results-found)]])

(defn entity-type-results-view
  [entity-type expanded? results has-more?]
  [:div
   {:class "px-6 py-3"}
   [:div
    {:class "font-bold text-2xl"}
    (->> entity-type
         (get entity-type-names/plurals)
         :field/translations
         :en)]
   [:div
    [:div
     [:ul
      {:class "grid lg:grid-cols-2 gap-2 pt-3"}
      (->>
       results
       (map
        (fn [entity]
          ^{:key (str "power-search-" entity-type "-result-" (:db/id entity))}

          [:li
           {:class "p-2 flex items-base gap-3 hover:bg-primary-light rounded cursor-pointer group"}
           [:div
            [:i
             {:class-name (fu/cs "fal"
                                 (->> entity-types/entities
                                      entity-type
                                      :entity/icon))}]]
           [:div
            {:class "flex flex-col gap-1 font-bold group-hover:text-base-light-text-accent"}
            [:a
             {:href     "#"
              :class "text-base-regular-text group-hover:text-base-regular-text-accent"
              :on-click (fn [e]
                          (routing/open-entity entity)
                          (.preventDefault e)
                          (.stopPropagation e))}
             [:span (entity/resolve-entity-name entity session/get-entity session/reverse-lookup)]]
            [:span
             {:class "m-0 text-sm font-normal group-hover:[&>span>a]:text-base-regular-text-accent"}
             (eu/entity-contact-view entity session/get-entity session/reverse-lookup)]]]))
       (doall))]]]
   (when (and (not expanded?) has-more?)
     [:div
      [:a
       {:class "text-base-light-text-accent underline"
        :href     "#"
        :on-click #(swap! state assoc :expanded-kw entity-type)}
       (loc/tr :see-all)]])])

(defn entity-type-results
  [entity-type search expanded?]
  (let [entities @(r/track session/get-entities entity-type)
        lazy-results
        (->>
         entities
         (sort-by :common/created-on)
         (reverse)
         (filter
          (fn [e]
            (case (:entity/type e)
              :entity.type/person
              (let [e (ic/enrich-entity e)]
                (or (when-let [lc (:common/lower-case-str e)]
                      (string/starts-with? lc search))
                    (when-let [first-name (:person/first-name e)]
                      (string/starts-with? (cu/str-trim-lcase first-name) search))
                    (when-let [last-name (:person/last-name e)]
                      (string/starts-with? (cu/str-trim-lcase last-name) search))
                    (when-let [email (:person/universal-email-address e)]
                      (string/starts-with? (cu/str-trim-lcase email) search))))
              (if-let [lcase (:common/lower-case-str e)]
                (string/starts-with? lcase search)
                (string/starts-with?
                 (cu/str-trim-lcase
                  (entity/resolve-entity-name e session/get-entity session/reverse-lookup))
                 search))))))
        results (if expanded?
                  lazy-results
                  (take 4 lazy-results))
        has-more? (seq (drop 4 lazy-results))]
    (when (seq results)
      [entity-type-results-view entity-type expanded? results has-more?])))

(defn search-results-view
  [state]
  (let [{:keys [search expanded-kw]} @state]
    [:div
     {:class "p-4"}
     (if expanded-kw
       [entity-type-results expanded-kw search true]
       (let [results-hiccup (->> searchable-entity-types
                                 (sort-by loc/tr)
                                 (keep
                                  (fn [entity-type]
                                    (let [results (entity-type-results entity-type search false)]
                                      (when (seq results)
                                        [:div
                                         {:key (str "search-results-" entity-type)}
                                         results]))))
                                 (doall))]
         (if (seq results-hiccup)
           results-hiccup
           [info-block-no-result-found])))]))

(defn results-dropdown
  [state]
  (let [search-str (:search @state)
        trimmed-search (cu/str-trim search-str)]
    [:div
     {:class-name (fu/cs "power-search-results")}
     [:div.power-search-results__drop-down
      [:div.power-search-results__drop-down-header
       [:div
        (cond
          ;;first cond
          (:expanded-kw @state)
          [:span.clickable
           {:on-click (fn [_]
                        (swap! state dissoc :expanded-kw))}
           [:i.fal.fa-chevron-left]
           [:span.d-inline-block.pl-3
            (loc/tr :back-to-search-results [trimmed-search])]]

          ;;second cond
          (empty? trimmed-search)
          [:span (loc/tr :power-search/dropdown-tooltip)]
          (< (count trimmed-search) 3)
          [:span (loc/tr :type-something-to-start-search [trimmed-search])]

          :else
          [:span (loc/tr :searching-for [trimmed-search])])]
       [:i.fal.fa-times.power-search-results__drop-down-toggle
        {:on-click (fn [e]
                     (end-search! 0))}]]
      (cond (empty? trimmed-search)
            [recent-view state]
            (< (count trimmed-search) 3)
            [info-block-more-chars]
            :else
            [search-results-view state])]]))

(defn power-search
  [open]
  (r/create-class
   {:component-did-mount
    (fn [_]
      (routing/add-change-interceptor :power-search-interceptor (partial end-search! 0)))
    :component-will-unmount
    (fn [_]
      (routing/remove-change-interceptor :power-search-interceptor)
      (goog-events/unlistenByKey (:blur-emulator @state)))
    :reagent-render
    (fn [open]
      (let [{:keys [search results expanded-kw]} @state
            searching? @app-db/searching]
        [:div
         {:class-name (fu/cs "power-search")}
         [:div.power-search-content
          [search-input stop-propagation-event! search (or (:search-open? @state) false)]
          (when searching?
            [results-dropdown state])]
         (if-not (:search-open? @state)
           [:i.fal.fa-search.power-search-toggle.global-navbar-icon
            {:on-click (fn [e]
                         (start-search!))}]
           [:i.fal.fa-times.power-search-toggle
            {:on-click (fn [e]
                         (end-search! 0))}])]))}))
